/**
 * Risk Profile Wizard
 * Full-screen wizard using generic framework
 * Preserves all existing features: PDF download, extraction review, auto-save
 * TASK-377: KYC approval gate + auto-prefill from approved KYC data
 */

import React, { useEffect, useState } from 'react';
import { API_CONFIG } from '../../config/api-config';
import { Wizard } from '../wizard';
import { createRiskProfileWizardConfig } from './riskProfileWizardConfig';
import riskProfileService, {
  getInferredRiskAnswers,
  confirmInferredAnswers,
  type InferredAnswerState,
} from '../../services/riskProfileService';
import { InferredAnswersContext, inferredValueAt } from './InferredAnswers';
import { prefillRiskProfile } from '../../services/formGenerationService';
import { toast } from 'sonner';
import { CompleteRiskProfile } from './schemas/riskProfileSchema';
import { Lock, AlertCircle } from 'lucide-react';

interface RiskProfileWizardProps {
  clientId: string;
  clientName: string;
  initialData?: Partial<CompleteRiskProfile>;
  /** When true, KYC is approved and editing is allowed */
  kycApproved?: boolean;
  onClose?: () => void;
  onComplete?: (data: CompleteRiskProfile) => void;
}

export const RiskProfileWizard: React.FC<RiskProfileWizardProps> = ({
  clientId,
  clientName,
  initialData,
  kycApproved = true,
  onClose,
  onComplete
}) => {
  const [prefillData, setPrefillData] = useState<Partial<CompleteRiskProfile> | null>(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [, setInheritedFields] = useState<string[]>([]);

  // TASK-619: Section-2 answers inferred from the KYC graph. Values render in
  // the review panel; they only become FinSA answers on RM confirm (the
  // engine treats unconfirmed inferred values as blank server-side).
  const [inferredStates, setInferredStates] = useState<Record<string, InferredAnswerState>>({});
  const [inferredValues, setInferredValues] = useState<Record<string, unknown>>({});
  const [notDerivable, setNotDerivable] = useState<Array<{ path: string; reason: string }>>([]);

  useEffect(() => {
    if (!kycApproved) return;
    getInferredRiskAnswers(clientId)
      .then((res) => {
        setInferredStates(res.answer_states || {});
        // TASK-846: section 3 carries the net worth the KYC file attests. Merged into one
        // lookup object because `inferredValueAt` walks a path with its `sectionN.` prefix
        // stripped — the two sections' leaves do not collide.
        setInferredValues({ ...(res.section2 || {}), ...(res.section3 || {}) });
        setNotDerivable(res.not_derivable || []);
      })
      .catch((err) => console.error('[TASK-619] inferred answers fetch failed:', err));
  }, [clientId, kycApproved]);

  const confirmPaths = async (paths: string[]) => {
    const confirm = paths.map((path) => ({
      path,
      value: inferredValueAt(inferredValues, path),
    }));
    const res = await confirmInferredAnswers(clientId, confirm);
    setInferredStates(res.answer_states || {});
    toast.success(`${paths.length} inferred answer${paths.length !== 1 ? 's' : ''} confirmed`);
  };

  const inferredCtx = {
    states: inferredStates,
    values: inferredValues,
    notDerivable,
    confirmOne: (path: string) => confirmPaths([path]),
    confirmAll: () =>
      confirmPaths(
        Object.entries(inferredStates)
          .filter(([, s]) => s.state === 'inferred')
          .map(([p]) => p)
      ),
    overrideOne: async (path: string) => {
      const res = await confirmInferredAnswers(clientId, [], [path]);
      setInferredStates(res.answer_states || {});
    },
  };

  /**
   * TASK-377: On init, call prefillRiskProfile to auto-populate sections 1 & 3
   * from the approved KYC data.
   */
  useEffect(() => {
    if (!kycApproved) return;

    const loadPrefill = async () => {
      setPrefillLoading(true);
      try {
        const response = await prefillRiskProfile(clientId);
        const data = response.data;

        if (data && data.kyc_approved) {
          // Merge prefilled KYC data with any existing initial data
          const section1Values: Record<string, any> = {};
          const section3Values: Record<string, any> = {};

          // Extract plain values from metadata-wrapped fields
          for (const [key, meta] of Object.entries(data.section_1 || {})) {
            const fieldMeta = meta as { value: any; source: string; confidence: number };
            section1Values[key] = fieldMeta.value;
          }
          for (const [key, meta] of Object.entries(data.section_3 || {})) {
            const fieldMeta = meta as { value: any; source: string; confidence: number };
            section3Values[key] = fieldMeta.value;
          }

          const merged: Partial<CompleteRiskProfile> = {
            ...initialData,
            section1: {
              ...section1Values,
              ...initialData?.section1,
            } as any,
            section3: {
              ...section3Values,
              ...initialData?.section3,
            } as any,
          };

          setPrefillData(merged);
          setInheritedFields(data.inherited_fields || []);

          if (data.inherited_fields?.length > 0) {
            toast.info('KYC data pre-filled', {
              description: `${data.inherited_fields.length} fields auto-populated from approved KYC profile`,
              duration: 4000,
            });
          }
        } else {
          setPrefillData(initialData || null);
        }
      } catch (error) {
        console.error('[TASK-377] Failed to prefill from KYC:', error);
        // Fall back to initial data on error
        setPrefillData(initialData || null);
      } finally {
        setPrefillLoading(false);
      }
    };

    loadPrefill();
  }, [clientId, kycApproved]);

  // TASK-377: Show locked state when KYC is not approved
  if (!kycApproved) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Risk Profile Locked
          </h2>
          <p className="text-gray-600 mb-6">
            The KYC profile must be approved before the Risk Profile can be edited.
            Sections 1 and 3 are auto-populated from approved KYC data.
          </p>
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Please ensure the KYC profile is submitted and approved by a Compliance
              Officer before proceeding with the Risk Profile questionnaire.
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-kielis-navy text-white rounded-lg hover:bg-opacity-90 font-medium"
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show loading while prefill is in progress
  if (prefillLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50">
        <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center">
          <div className="animate-spin mx-auto w-10 h-10 border-4 border-kielis-gold border-t-transparent rounded-full mb-4" />
          <p className="text-gray-600 font-medium">Loading KYC data...</p>
          <p className="text-sm text-gray-500 mt-1">
            Pre-filling sections from approved KYC profile
          </p>
        </div>
      </div>
    );
  }
  /**
   * Download Blank PDF
   * Generates empty form for manual completion
   * Preserved from InvestmentRiskProfileForm.tsx lines 183-211
   */
  const handleDownloadBlankPDF = async () => {
    try {
      console.log('[BLANK PDF] Starting blank PDF generation for client:', clientId);

      toast.info('Generating blank PDF...', {
        description: 'Creating empty form for manual completion...',
        duration: 2000
      });

      // Generate blank PDF
      console.log('[BLANK PDF] Calling generateBlankPDF service...');
      const result = await riskProfileService.generateBlankPDF(clientId);
      console.log('[BLANK PDF] Service returned:', result);

      // Fetch the PDF with authentication headers
      await downloadPDFFile(result.pdf_url, result.filename);

      toast.success('Blank PDF Downloaded!', {
        description: `${result.filename} (${result.page_count} pages)`,
        duration: 4000
      });
    } catch (error) {
      console.error('[BLANK PDF] Failed to download blank PDF:', error);
      toast.error('Failed to generate blank PDF', {
        description: 'Please try again or contact support.',
        duration: 4000
      });
    }
  };

  /**
   * Download Filled PDF
   * Generates PDF with all current form data
   * Preserved from InvestmentRiskProfileForm.tsx lines 213-246
   */
  const handleDownloadFilledPDF = async () => {
    try {
      console.log('[FILLED PDF] Starting filled PDF generation for client:', clientId);

      toast.info('Generating PDF...', {
        description: 'Saving and creating your risk profile document...',
        duration: 2000
      });

      // Generate the PDF with current data
      console.log('[FILLED PDF] Calling generatePDF service...');
      const result = await riskProfileService.generatePDF(clientId);
      console.log('[FILLED PDF] Service returned:', result);

      // Fetch the PDF with authentication headers
      await downloadPDFFile(result.pdf_url, result.filename);

      toast.success('PDF Downloaded!', {
        description: `${result.filename} (${result.page_count} pages)`,
        duration: 4000
      });
    } catch (error) {
      console.error('[FILLED PDF] Failed to download PDF:', error);
      toast.error('Failed to generate PDF', {
        description: 'Please try again or contact support.',
        duration: 4000
      });
    }
  };

  /**
   * Helper function to download PDF with authentication
   * Preserved from InvestmentRiskProfileForm.tsx lines 248-282
   */
  const downloadPDFFile = async (pdfUrl: string, filename: string) => {
    const apiBaseURL = API_CONFIG.GATEWAY_URL;
    const downloadUrl = `${apiBaseURL}${pdfUrl}`;

    const token = localStorage.getItem('auth_token');
    const accountId = localStorage.getItem('account_id');

    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(accountId && { 'X-Account-ID': accountId })
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download PDF: ${response.statusText}`);
    }

    // Create blob from response
    const blob = await response.blob();

    // Create object URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL
    window.URL.revokeObjectURL(blobUrl);
  };

  /**
   * Create wizard configuration with PDF handlers
   */
  const config = createRiskProfileWizardConfig(
    clientId,
    handleDownloadBlankPDF,
    handleDownloadFilledPDF
  );

  /**
   * Handle completion
   * Calls both wizard's onComplete and optional prop callback
   */
  const handleComplete = async (data: CompleteRiskProfile) => {
    // Call wizard's onComplete (handles backend submission)
    if (config.onComplete) {
      await config.onComplete(data);
    }

    // Call optional prop callback
    if (onComplete) {
      onComplete(data);
    }
  };

  // Update config with completion handler
  const configWithCallback = {
    ...config,
    onComplete: handleComplete
  };

  // TASK-377: Use prefilled data (merged KYC + initial) when available
  const effectiveInitialData = prefillData || initialData;

  return (
    <InferredAnswersContext.Provider value={inferredCtx}>
      <Wizard<CompleteRiskProfile>
        clientId={clientId}
        clientName={clientName}
        config={configWithCallback}
        initialData={effectiveInitialData}
        onClose={onClose}
        reviewMode={false}
      />
    </InferredAnswersContext.Provider>
  );
};

export default RiskProfileWizard;
